import React, { useEffect, useMemo, useState } from "react";
import {
  getKarmaMe,
  getKarmaLedger,
  listRewards,
  listRedemptions,
  redeemReward,
} from "@/services/rewards.service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Ticket, Filter } from "lucide-react";
import KarmaHero from "@/components/rewards/KarmaHero";
import RewardCard from "@/components/rewards/RewardCard";
import RedemptionRow from "@/components/rewards/RedemptionRow";

const HOW_TO_EARN = [
  { label: "Release payment within 24h of submission (payer)", delta: "+10" },
  { label: "Release payment within 3 days (payer)", delta: "+5" },
  { label: "Complete a contract with zero disputes (both parties)", delta: "+20" },
  { label: "Resolve a dispute without escalation (both parties)", delta: "+5" },
  { label: "Approve work >7 days after submission (payer)", delta: "-10" },
  { label: "A dispute is resolved against you", delta: "-15" },
];

const Rewards = () => {
  const [karma, setKarma] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [howOpen, setHowOpen] = useState(false);
  const [cat, setCat] = useState("all");
  const [minCost, setMinCost] = useState("");
  const [maxCost, setMaxCost] = useState("");
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [sort, setSort] = useState("recommended");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [k, r, rd, l] = await Promise.all([
      getKarmaMe(),
      listRewards(),
      listRedemptions(),
      getKarmaLedger(),
    ]);
    setKarma(k);
    setRewards(r);
    setRedemptions(rd);
    setLedger(l);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const categories = useMemo(() => Array.from(new Set(rewards.map((r) => r.category))), [rewards]);
  const balance = karma?.balance ?? 0;

  const filtered = useMemo(() => {
    let list = rewards.filter((r) => {
      if (cat !== "all" && r.category !== cat) return false;
      if (minCost && r.karma_cost < parseInt(minCost)) return false;
      if (maxCost && r.karma_cost > parseInt(maxCost)) return false;
      if (eligibleOnly && r.karma_cost > balance) return false;
      return true;
    });
    if (sort === "lowest") list.sort((a, b) => a.karma_cost - b.karma_cost);
    else if (sort === "newest") list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || a.karma_cost - b.karma_cost);
    return list;
  }, [rewards, cat, minCost, maxCost, eligibleOnly, sort, balance]);

  const featured = useMemo(() => rewards.filter((r) => r.featured).slice(0, 6), [rewards]);

  const redeem = async () => {
    if (!confirm) return;
    setSaving(true);
    try {
      await redeemReward(confirm.reward_id, {});
      toast.success("Reward redeemed! Check your email.");
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="rewards-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Rewards</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Karma rewards</h1>
        <p className="mt-2 text-sm text-muted-foreground">Earn Karma by paying on time and shipping clean work. Redeem it for partner perks.</p>
      </div>

      <KarmaHero karma={karma} onHow={() => setHowOpen(true)} />

      {featured.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-semibold">Featured</h2>
            <span className="text-xs text-muted-foreground">Curated partner picks</span>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1" data-testid="featured-row">
            {featured.map((r) => (
              <div key={r.reward_id} className="min-w-[260px] max-w-[260px]">
                <RewardCard reward={r} canAfford={balance >= r.karma_cost} onRedeem={setConfirm} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Category</label>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-9 mt-1 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="rewards-filter-category">
            <option value="all">All</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Min karma</label>
          <Input type="number" min="0" value={minCost} onChange={(e) => setMinCost(e.target.value)} className="h-9 mt-1" data-testid="rewards-filter-min" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Max karma</label>
          <Input type="number" min="0" value={maxCost} onChange={(e) => setMaxCost(e.target.value)} className="h-9 mt-1" data-testid="rewards-filter-max" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Sort</label>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 mt-1 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="rewards-filter-sort">
            <option value="recommended">Recommended</option>
            <option value="lowest">Lowest cost</option>
            <option value="newest">Newest</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium sm:mt-5" data-testid="rewards-filter-eligible-label">
          <input type="checkbox" checked={eligibleOnly} onChange={(e) => setEligibleOnly(e.target.checked)} className="accent-primary" data-testid="rewards-filter-eligible" />
          Eligible now
        </label>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Filter className="w-8 h-8 mx-auto text-muted-foreground/60" />
          <div className="mt-3 text-muted-foreground" data-testid="rewards-empty">No rewards match these filters.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="rewards-grid">
          {filtered.map((r) => (
            <RewardCard key={r.reward_id} reward={r} canAfford={balance >= r.karma_cost} onRedeem={setConfirm} />
          ))}
        </div>
      )}

      {/* My Rewards */}
      <div>
        <h2 className="font-display text-xl font-semibold mb-3 inline-flex items-center gap-2"><Ticket className="w-5 h-5" /> My Rewards</h2>
        <div className="bg-card border border-border rounded-xl">
          {redemptions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm" data-testid="my-rewards-empty">Nothing redeemed yet.</div>
          ) : (
            <div data-testid="my-rewards-list">
              {redemptions.map((r) => <RedemptionRow key={r.redemption_id} r={r} />)}
            </div>
          )}
        </div>
      </div>

      {/* Karma activity */}
      {ledger.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-semibold mb-3">Karma activity</h2>
          <div className="bg-card border border-border rounded-xl divide-y divide-border" data-testid="karma-ledger">
            {ledger.slice(0, 12).map((e) => (
              <div key={e.entry_id} className="flex items-center justify-between px-4 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{e.event}</div>
                  {e.note && <div className="text-xs text-muted-foreground truncate">{e.note}</div>}
                </div>
                <div className={`text-right font-display font-bold shrink-0 ${e.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {e.delta > 0 ? "+" : ""}{e.delta}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirm redeem modal */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent data-testid="redeem-confirm-modal">
          <DialogHeader><DialogTitle>Redeem reward</DialogTitle></DialogHeader>
          {confirm && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {confirm.image_url && <img src={confirm.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{confirm.partner_name}</div>
                  <div className="font-display font-semibold">{confirm.title}</div>
                </div>
              </div>
              <div className="flex items-center justify-between bg-secondary/40 rounded-lg p-3 text-sm">
                <span>Karma cost</span>
                <span className="font-bold">{confirm.karma_cost}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Balance after</span>
                <span className="font-bold">{balance - confirm.karma_cost}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">{confirm.redemption_instructions}</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={redeem} disabled={saving} className="w-full" data-testid="confirm-redeem-btn">
              {saving ? "Redeeming…" : "Confirm redemption"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* How to earn modal */}
      <Dialog open={howOpen} onOpenChange={setHowOpen}>
        <DialogContent data-testid="how-to-earn-modal">
          <DialogHeader><DialogTitle>How to earn Karma</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground mb-1">Both payers and payees earn Karma. Payers earn for releasing payments fast; both sides share the clean-completion bonus and dispute-resolution credit.</p>
            {HOW_TO_EARN.map((row) => (
              <div key={row.label} className="flex items-center justify-between bg-secondary/30 rounded-lg p-3 text-sm">
                <span>{row.label}</span>
                <span className={`font-bold ${row.delta.startsWith("-") ? "text-red-600" : "text-emerald-600"}`}>{row.delta}</span>
              </div>
            ))}
          </div>
          <div className="text-xs text-muted-foreground">Tiers: Bronze 0 · Silver 100 · Gold 500.</div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Rewards;
